import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import Icon from "../../../components/general/Icon";
import ErrorMessage from "../ErrorMessage";
import { cn } from "@babybjorn/ui";
import * as s from "./Input.module.scss";

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
	error?: boolean;
	hiddenLabel?: boolean;
	errorMessage?: string;
	label?: string | null;
	square?: boolean;
	overlayElement?: ReactNode;
	containerClassName?: string;
	variant?: "thin" | "default";
	search?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			id,
			error,
			label,
			errorMessage,
			square,
			hiddenLabel,
			containerClassName,
			className,
			overlayElement,
			variant,
			search,
			...rest
		}: InputProps,
		ref,
	) => {
		const InputContainerClassNames = cn(
			s.inputFieldContainer,
			containerClassName,
			{
				[s.invalid]: error === true,
				[s.square]: square,
			},
		);
		const ValidateIcon = error !== undefined && (
			<Icon
				icon={error ? "close" : "checkmark"}
				className={cn(s.validateIcon, { [s.invalid]: error })}
			/>
		);

		return (
			<div className={cn(s.formInputsWrapper, { [s.search]: search })}>
				{!hiddenLabel && label && (
					<label htmlFor={id} className={cn(s.label)}>
						{label}
					</label>
				)}
				<div className={InputContainerClassNames}>
					<input
						id={id}
						aria-label={hiddenLabel && label ? label : undefined}
						className={cn(s.input, className, {
							[s.square]: square,
							[s.thin]: variant === "thin",
							[s.error]: error !== undefined && error,
							[s.hasIcon]: error !== undefined && ValidateIcon,
						})}
						ref={ref}
						{...rest}
					/>
					{overlayElement}
					{error !== undefined && ValidateIcon}
				</div>
				{error === true && (
					<ErrorMessage message={errorMessage} className={cn(s.errorMessage)} />
				)}
			</div>
		);
	},
);

Input.displayName = "Input";

export default Input;
